// ═══════════════════════════════════════════════════════════════════════════════
// Accessible confirm dialog — light/dark via AppModal + AppButton
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, type ReactNode } from 'react';
import { AppButton } from './AppButton';
import { AppModal } from './AppModal';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'default';
  /** When true, confirm button is disabled (e.g. while a request is in flight). */
  confirmDisabled?: boolean;
  /** When true, cancel and backdrop close are inert; use with async confirm. */
  cancelDisabled?: boolean;
  /** Tailwind z-index on backdrop (e.g. z-[110] when stacking above another modal). */
  backdropZClass?: string;
  children?: ReactNode;
};

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  variant = 'default',
  confirmDisabled = false,
  cancelDisabled = false,
  backdropZClass = 'z-[60]',
  children,
}: ConfirmDialogProps) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      confirmRef.current?.focus();
    }
  }, [open]);

  return (
    <AppModal
      open={open}
      onClose={onCancel}
      title={title}
      titleId="confirm-dialog-title"
      describedById="confirm-dialog-desc"
      hideCloseButton
      closeAriaLabel={cancelLabel}
      closeDisabled={cancelDisabled}
      backdropZClass={backdropZClass}
    >
      <p id="confirm-dialog-desc" className="text-sm text-gray-600 dark:text-gray-300">
        {description}
      </p>
      {children}
      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <AppButton variant="secondary" onClick={onCancel} disabled={cancelDisabled}>
          {cancelLabel}
        </AppButton>
        <AppButton
          ref={confirmRef}
          variant={variant === 'danger' ? 'danger' : 'primary'}
          onClick={onConfirm}
          disabled={confirmDisabled}
        >
          {confirmLabel}
        </AppButton>
      </div>
    </AppModal>
  );
};
